/**
 * Ce que les SOUS-PAGES disent de la soirée.
 *
 * La page d'accueil est composée depuis le CRM et bascule en mode souvenirs
 * toute seule (`modePasse.ts`). Les sous-pages, elles, sont des mises en page
 * dédiées : « La prochaine page s'écrit le 3 octobre 2026. » et le bouton
 * « Réserver ma place » y étaient écrits EN DUR, et le bouton du menu
 * (`SubPageNav`) lisait les contenus par défaut du site — donc
 * `/#formules`, une ancre qui n'existe plus sur l'accueil une fois la soirée
 * passée.
 *
 * Constaté le 2026-10-05, deux jours après le Grand Gala : un lien mort dans
 * le menu de chacune des sous-pages, et deux pages qui annonçaient encore la
 * soirée de l'avant-veille comme à venir.
 *
 * Ces fonctions sont pures : les pages fournissent l'état réel de la soirée
 * (lu une fois, côté serveur ou via l'API publique), elles rendent le texte
 * et le lien qui vont avec.
 */

export interface EtatSoiree {
  /** Une soirée est-elle à venir (ou en cours) ? */
  aVenir: boolean
  /** Sa date telle qu'on l'affiche (« 3 octobre 2026 »), si on la connaît. */
  date?: string | null
}

/** « Rester informé » — la seule destination qui a du sens hors soirée. */
export const ANCRE_RESTER_INFORME = '/#rester-informe'

/**
 * La phrase de bas de page des sous-pages. Sans soirée annoncée, elle ne
 * nomme aucune date : une date passée au futur est pire que pas de date.
 */
export function phraseProchainePage(etat: EtatSoiree): string {
  const date = typeof etat.date === 'string' ? etat.date.trim() : ''
  if (etat.aVenir && date !== '') return `La prochaine page s'écrit le ${date}.`
  return "La prochaine page s'écrit bientôt."
}

export interface Lien {
  label: string
  href: string
}

/** Le bouton qui accompagne cette phrase : réserver, ou rester informé. */
export function boutonProchainePage(etat: EtatSoiree): Lien {
  return etat.aVenir
    ? { label: 'Réserver ma place', href: '/reservation' }
    : { label: 'Rester informé', href: ANCRE_RESTER_INFORME }
}

interface ContenuEnTete {
  ctaLabel?: unknown
  ctaHref?: unknown
  ctaLabelPast?: unknown
  ctaHrefPast?: unknown
}

function txt(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

/**
 * Le bouton du menu des sous-pages, ou `null` quand il n'y a rien à proposer.
 *
 * `aVenir` vaut `null` tant qu'on ne sait pas (lecture en cours, ou API
 * injoignable) : on ne montre alors AUCUN bouton. Inviter à réserver une
 * soirée terminée est une faute visible ; un bouton qui met une seconde à
 * apparaître ne l'est pas.
 */
export function ctaSousPage(contenu: ContenuEnTete, aVenir: boolean | null): Lien | null {
  if (aVenir === true) {
    const label = txt(contenu.ctaLabel)
    if (label === '') return null
    return { label, href: txt(contenu.ctaHref) || ANCRE_RESTER_INFORME }
  }
  if (aVenir === false) {
    const label = txt(contenu.ctaLabelPast)
    if (label === '') return null
    return { label, href: txt(contenu.ctaHrefPast) || ANCRE_RESTER_INFORME }
  }
  return null
}
